<!-- 📊 MGI Dashboard Analytics - Gestión de Incidencias para Consorcios de Bancas de Lotería -->
<template>
  <div class="mgi-dashboard-analytics">
    <!-- 📈 KPI Cards Row - Métricas del Negocio Real MGI -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3" v-for="(kpi, index) in mgiKpiData" :key="index">
        <v-card class="kpi-card" :color="kpi.color" variant="tonal">
          <v-card-text>
            <div class="d-flex align-center justify-between">
              <div class="kpi-content">
                <div class="kpi-label">{{ kpi.label }}</div>
                <div class="kpi-value">{{ kpi.value }}</div>
                <div class="kpi-change" :class="kpi.changeClass">
                  <v-icon size="small" :icon="kpi.changeIcon" />
                  {{ kpi.change }}
                </div>
                <div class="kpi-subtitle">{{ kpi.subtitle }}</div>
              </div>
              <v-avatar size="60" :color="kpi.color">
                <v-icon size="30" :icon="kpi.icon" />
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 📊 Main Charts Row - Analytics del Negocio Real MGI -->
    <v-row>
      <!-- 📈 Resolución de Incidencias por Hora -->
      <v-col cols="12" lg="8">
        <BaseChart
          title="Resolución de Incidencias por Hora"
          icon="mdi-clipboard-check"
          icon-color="primary"
          chart-type="line"
          :data="incidentsResolutionData"
          :options="incidentsResolutionOptions"
          :loading="loading"
          :error="error"
          :height="350"
          show-refresh
          show-download
          @refresh="refreshIncidentsData"
        />
      </v-col>

      <!-- 🍩 Distribución de Incidencias por Tipo -->
      <v-col cols="12" lg="4">
        <BaseChart
          title="Tipos de Incidencias"
          icon="mdi-chart-donut-variant"
          icon-color="warning"
          chart-type="doughnut"
          :data="incidentTypesData"
          :options="incidentTypesOptions"
          :loading="loading"
          :error="error"
          :height="350"
          show-refresh
          @refresh="refreshIncidentTypesData"
        />
      </v-col>
    </v-row>

    <!-- 📊 Secondary Charts Row - Métricas Operativas MGI -->
    <v-row class="mt-4">
      <!-- 📊 Estados de Agencias por Región -->
      <v-col cols="12" lg="6">
        <BaseChart
          title="Estados de Agencias por Región"
          icon="mdi-map-marker-multiple"
          icon-color="success"
          chart-type="bar"
          :data="agencyStatusData"
          :options="agencyStatusOptions"
          :loading="loading"
          :error="error"
          :height="300"
          show-refresh
          show-options
          @refresh="refreshAgencyStatusData"
        />
      </v-col>

      <!-- 📈 Eficiencia de Supervisores de Campo -->
      <v-col cols="12" lg="6">
        <BaseChart
          title="Eficiencia de Supervisores de Campo"
          icon="mdi-account-check"
          icon-color="info"
          chart-type="line"
          :data="supervisorEfficiencyData"
          :options="supervisorEfficiencyOptions"
          :loading="loading"
          :error="error"
          :height="300"
          show-refresh
          show-download
          @refresh="refreshSupervisorEfficiencyData"
        />
      </v-col>
    </v-row>

    <!-- 📊 Métricas Detalladas del Consorcio -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="me-2" color="primary">mdi-chart-box</v-icon>
            Métricas Detalladas del Consorcio
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3" v-for="metric in detailedMGIMetrics" :key="metric.title">
                <div class="metric-card">
                  <h4 class="metric-title">{{ metric.title }}</h4>
                  <div class="metric-value">{{ metric.value }}</div>
                  <div class="metric-subtitle">{{ metric.subtitle }}</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 🎯 Panel de Estados en Tiempo Real -->
    <v-row class="mt-4">
      <v-col cols="12" lg="8">
        <v-card>
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon class="me-2" color="success">mdi-pulse</v-icon>
              Estado del Sistema en Tiempo Real
            </div>
            <v-chip color="success" size="small">
              <v-icon start icon="mdi-circle" size="8"></v-icon>
              Sistema Operativo
            </v-chip>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6" md="3" v-for="status in systemStatus" :key="status.label">
                <div class="status-indicator">
                  <v-icon :color="status.color" size="large">{{ status.icon }}</v-icon>
                  <div class="status-label">{{ status.label }}</div>
                  <div class="status-value" :class="`text-${status.color}`">{{ status.value }}</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      
      <!-- 📋 Incidencias Críticas Recientes -->
      <v-col cols="12" lg="4">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="me-2" color="error">mdi-alert-circle</v-icon>
            Incidencias Críticas
          </v-card-title>
          <v-card-text>
            <v-list density="compact">
              <v-list-item
                v-for="incident in criticalIncidents"
                :key="incident.id"
                :prepend-icon="incident.icon"
                :title="incident.title"
                :subtitle="incident.agency"
                class="mb-2"
              >
                <template v-slot:append>
                  <v-chip :color="incident.priority" size="x-small">
                    {{ incident.priority }}
                  </v-chip>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { NotificationService } from '@/services/notification.service'

// 📊 Estado reactivo
const loading = ref(false)
const error = ref<string | undefined>(undefined)

// 📊 MGI KPI Data - Métricas del Negocio Real
const mgiKpiData = computed(() => [
  {
    label: 'Agencias Activas',
    value: '2,487',
    change: '+15 nuevas',
    changeClass: 'text-success',
    changeIcon: 'mdi-trending-up',
    icon: 'mdi-storefront',
    color: 'primary',
    subtitle: 'de 2,500 total'
  },
  {
    label: 'Incidencias Pendientes',
    value: '127',
    change: '-23%',
    changeClass: 'text-success',
    changeIcon: 'mdi-trending-down',
    icon: 'mdi-alert-circle',
    color: 'warning',
    subtitle: 'en las últimas 24h'
  },
  {
    label: 'Supervisores Activos',
    value: '89',
    change: '+5%',
    changeClass: 'text-success',
    changeIcon: 'mdi-trending-up',
    icon: 'mdi-account-hard-hat',
    color: 'success',
    subtitle: 'en campo hoy'
  },
  {
    label: 'Eficiencia Promedio',
    value: '87.3%',
    change: '+2.1%',
    changeClass: 'text-success',
    changeIcon: 'mdi-trending-up',
    icon: 'mdi-chart-line-variant',
    color: 'info',
    subtitle: 'resolución de incidencias'
  }
])

// 📈 Datos de resolución de incidencias por hora
const incidentsResolutionData = computed(() => ({
  labels: ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'],
  datasets: [
    {
      label: 'Incidencias Reportadas',
      data: [15, 23, 18, 27, 31, 19, 25, 22, 16, 12, 8],
      backgroundColor: 'rgba(255, 152, 0, 0.1)',
      borderColor: '#FF9800',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    },
    {
      label: 'Incidencias Resueltas',
      data: [8, 15, 12, 20, 24, 18, 23, 20, 14, 10, 7],
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      borderColor: '#4CAF50',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
}))

const incidentsResolutionOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    intersect: false,
    mode: 'index' as const
  },
  plugins: {
    legend: {
      display: true,
      position: 'top' as const
    },
    tooltip: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleColor: 'white',
      bodyColor: 'white'
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: (value: any) => `${value} incidencias`
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
}))

// 🍩 Distribución de incidencias por tipo - REAL MGI
const incidentTypesData = computed(() => ({
  labels: [
    'Personal (Sin empleada)',
    'Averías de Internet',
    'Problemas Eléctricos',
    'Equipos Dañados',
    'Infraestructura',
    'Excepcionales'
  ],
  datasets: [{
    data: [35, 28, 18, 12, 5, 2],
    backgroundColor: [
      '#FF5722', // Personal (crítico)
      '#FF9800', // Internet (frecuente)
      '#FFC107', // Eléctricos
      '#2196F3', // Equipos
      '#4CAF50', // Infraestructura
      '#9C27B0'  // Excepcionales
    ],
    borderWidth: 2,
    borderColor: '#ffffff'
  }]
}))

const incidentTypesOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        padding: 15,
        font: {
          size: 11
        }
      }
    },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          const label = context.label || ''
          const value = context.parsed || 0
          const total = context.dataset.data.reduce((sum: number, val: number) => sum + val, 0)
          const percentage = ((value / total) * 100).toFixed(1)
          return `${label}: ${value} (${percentage}%)`
        }
      }
    }
  }
}))

// 📊 Estados de agencias por región - REPÚBLICA DOMINICANA
const agencyStatusData = computed(() => ({
  labels: ['Santo Domingo', 'Santiago', 'La Romana', 'Puerto Plata', 'San Pedro', 'Barahona'],
  datasets: [
    {
      label: 'Operativas',
      data: [850, 420, 280, 190, 165, 145],
      backgroundColor: '#4CAF50',
      borderRadius: 4
    },
    {
      label: 'Con Incidencias',
      data: [45, 28, 15, 12, 8, 6],
      backgroundColor: '#FF9800',
      borderRadius: 4
    },
    {
      label: 'Críticas',
      data: [8, 5, 3, 2, 1, 1],
      backgroundColor: '#F44336',
      borderRadius: 4
    }
  ]
}))

const agencyStatusOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top' as const
    }
  },
  scales: {
    x: {
      stacked: false
    },
    y: {
      stacked: false,
      beginAtZero: true,
      ticks: {
        callback: (value: any) => `${value} agencias`
      }
    }
  }
}))

// 📈 Eficiencia de supervisores de campo
const supervisorEfficiencyData = computed(() => ({
  labels: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
  datasets: [
    {
      label: 'Eficiencia Promedio (%)',
      data: [85.2, 87.8, 89.1, 86.5, 88.9, 82.3, 79.1],
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
      borderColor: '#2196F3',
      borderWidth: 3,
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#2196F3',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointRadius: 6
    }
  ]
}))

const supervisorEfficiencyOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: {
      beginAtZero: false,
      min: 70,
      max: 100,
      ticks: {
        callback: (value: any) => `${value}%`
      }
    }
  }
}))

// 📊 Métricas detalladas del consorcio
const detailedMGIMetrics = computed(() => [
  {
    title: 'Tiempo Promedio de Resolución',
    value: '2.3 horas',
    subtitle: 'incidencias críticas'
  },
  {
    title: 'Cobertura Semanal',
    value: '94.2%',
    subtitle: 'agencias visitadas'
  },
  {
    title: 'Incidencias Resueltas Hoy',
    value: '156',
    subtitle: 'de 183 reportadas'
  },
  {
    title: 'Supervisores en Ruta',
    value: '67',
    subtitle: 'de 89 activos'
  }
])

// 🎯 Estados del sistema en tiempo real
const systemStatus = computed(() => [
  {
    label: 'Conectividad',
    value: '99.8%',
    icon: 'mdi-wifi',
    color: 'success'
  },
  {
    label: 'Agencias Online',
    value: '2,456',
    icon: 'mdi-storefront-check',
    color: 'success'
  },
  {
    label: 'Supervisores Activos',
    value: '89',
    icon: 'mdi-account-check',
    color: 'info'
  },
  {
    label: 'Respuesta Promedio',
    value: '1.2s',
    icon: 'mdi-speedometer',
    color: 'success'
  }
])

// 📋 Incidencias críticas recientes - REALES PARA BANCAS
const criticalIncidents = computed(() => [
  {
    id: 1,
    title: 'Sin empleada - Agencia Centro',
    agency: 'Plaza Central, Santo Domingo',
    priority: 'error',
    icon: 'mdi-account-alert'
  },
  {
    id: 2,
    title: 'Avería eléctrica zona norte',
    agency: 'Múltiples agencias Santiago',
    priority: 'warning',
    icon: 'mdi-flash-alert'
  },
  {
    id: 3,
    title: 'Internet caído - Cluster Este',
    agency: '8 agencias La Romana',
    priority: 'error',
    icon: 'mdi-wifi-off'
  },
  {
    id: 4,
    title: 'Puerta trabada',
    agency: 'Agencia Villa Mella',
    priority: 'warning',
    icon: 'mdi-door-closed-lock'
  }
])

// 🔄 Métodos de refresco para datos MGI
const refreshIncidentsData = async () => {
  loading.value = true
  try {
    // Simular llamada a API del field supervisor
    await new Promise(resolve => setTimeout(resolve, 1000))
    // En producción: await api.get('/field-supervisor/incidents-analytics')
    NotificationService.success('Datos de incidencias actualizados')
  } catch (err) {
    error.value = 'Error al cargar datos de incidencias'
    NotificationService.error('Error al actualizar datos de incidencias')
  } finally {
    loading.value = false
  }
}

const refreshIncidentTypesData = async () => {
  loading.value = true
  try {
    await new Promise(resolve => setTimeout(resolve, 800))
    // En producción: await api.get('/analytics/incident-types-distribution')
    NotificationService.success('Distribución de tipos actualizada')
  } catch (err) {
    error.value = 'Error al cargar tipos de incidencias'
    NotificationService.error('Error al actualizar distribución de tipos')
  } finally {
    loading.value = false
  }
}

const refreshAgencyStatusData = async () => {
  loading.value = true
  try {
    await new Promise(resolve => setTimeout(resolve, 1200))
    // En producción: await api.get('/analytics/agency-status-by-region')
    NotificationService.success('Estados de agencias actualizados')
  } catch (err) {
    error.value = 'Error al cargar estados de agencias'
    NotificationService.error('Error al actualizar estados de agencias')
  } finally {
    loading.value = false
  }
}

const refreshSupervisorEfficiencyData = async () => {
  loading.value = true
  try {
    await new Promise(resolve => setTimeout(resolve, 600))
    // En producción: await api.get('/analytics/supervisor-efficiency')
    NotificationService.success('Eficiencia de supervisores actualizada')
  } catch (err) {
    error.value = 'Error al cargar eficiencia de supervisores'
    NotificationService.error('Error al actualizar eficiencia')
  } finally {
    loading.value = false
  }
}

const refreshAllMGIData = async () => {
  await Promise.all([
    refreshIncidentsData(),
    refreshIncidentTypesData(),
    refreshAgencyStatusData(),
    refreshSupervisorEfficiencyData()
  ])
}

// 🔄 Lifecycle
onMounted(() => {
  // Auto-refresh cada minuto en producción
  // startAutoRefresh(refreshAllMGIData, 60000)
})

onUnmounted(() => {
  // stopAutoRefresh()
})
</script>

<style scoped>
.mgi-dashboard-analytics {
  padding: 20px;
}

/* 📊 KPI Cards */
.kpi-card {
  height: 140px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}

.kpi-content {
  flex: 1;
}

.kpi-label {
  font-size: 0.875rem;
  opacity: 0.8;
  margin-bottom: 4px;
  font-weight: 500;
}

.kpi-value {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 4px;
  line-height: 1;
}

.kpi-change {
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 2px;
}

.kpi-subtitle {
  font-size: 0.7rem;
  opacity: 0.7;
  font-weight: 400;
}

/* 📊 Métricas detalladas */
.metric-card {
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.5);
  border: 1px solid rgba(var(--v-theme-outline), 0.12);
  transition: all 0.2s ease;
}

.metric-card:hover {
  background: rgba(var(--v-theme-primary), 0.04);
  border-color: rgba(var(--v-theme-primary), 0.24);
}

.metric-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 8px;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 4px;
}

.metric-subtitle {
  font-size: 0.75rem;
  opacity: 0.7;
  font-weight: 400;
}

/* 🎯 Estados del sistema */
.status-indicator {
  text-align: center;
  padding: 12px;
}

.status-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin: 8px 0 4px 0;
}

.status-value {
  font-size: 1.25rem;
  font-weight: 600;
}

/* 📱 Responsive */
@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }
  
  .metric-value {
    font-size: 1.25rem;
  }
  
  .mgi-dashboard-analytics {
    padding: 12px;
  }
}

/* 🎨 Tema específico para MGI */
.v-theme--light .metric-card {
  background: rgba(255, 255, 255, 0.8);
  border-color: rgba(0, 0, 0, 0.06);
}

.v-theme--dark .metric-card {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.12);
}
</style>
